// Control Center (nur Super Admin) › Briefversand-Kosten: Vertragspreise eintragen, Kosten je Monat und Objekt.
// Björn/Team sehen keine Kosten – deshalb nur hier und nicht im Postausgang.
import { useEffect, useMemo, useState } from 'react';
import { CurrencyEur } from '@phosphor-icons/react';
import { CcWidget, KopfIcon } from './Rahmen';
import { ladePostausgang, monatName } from '../../../lib/epost/postausgang';
import { LEERE_PREISE, PREIS_FELDER, euro, kostenUebersicht, ladePreise, speicherePreise, type EpostPreise, type KostenBrief } from '../../../lib/epost/kosten';

const feld = { height: 36, width: 96, padding: '0 10px', borderRadius: 10, border: 'none', outline: 'none', background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 13.5, color: 'var(--ink)', textAlign: 'right' as const };

export function EpostKostenWidget({ spalte }: { spalte: string }) {
  const [preise, setPreise] = useState<EpostPreise>(LEERE_PREISE);
  const [roh, setRoh] = useState<Record<string, string>>({});
  const [briefe, setBriefe] = useState<KostenBrief[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState(false);
  const [offenerMonat, setOffenerMonat] = useState<string | null>(null);

  useEffect(() => {
    void ladePreise().then((p) => { setPreise(p); setRoh(Object.fromEntries(Object.entries(p).map(([k, v]) => [k, v ? String(v).replace('.', ',') : '']))); }, (e: Error) => setFehler(e.message));
    void ladePostausgang(400).then((b) => setBriefe(b as unknown as KostenBrief[]), (e: Error) => setFehler(e.message));
  }, []);

  const uebersicht = useMemo(() => (briefe ? kostenUebersicht(briefe, preise) : null), [briefe, preise]);
  const ohnePreise = !preise.grund;

  const speichern = (key: keyof EpostPreise) => {
    const wert = Number((roh[key] ?? '').replace(',', '.')) || 0;
    const neu = { ...preise, [key]: wert };
    setPreise(neu);
    void speicherePreise(neu).then(() => { setGespeichert(true); setTimeout(() => setGespeichert(false), 1500); }, (e: Error) => setFehler(e.message));
  };

  return (
    <CcWidget titel="Briefversand – Kosten" untertitel="Nur für dich sichtbar · nach deinen Vertragspreisen" spalte={spalte}
      icon={<KopfIcon flaeche="var(--ok)" farbe="#fff"><CurrencyEur size={19} weight="fill" /></KopfIcon>}
      rechts={gespeichert ? <span role="status" style={{ fontSize: 12, fontWeight: 800, color: 'var(--ok)' }}>gespeichert</span> : undefined}>
      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)' }}>Preise laut Vertrag (netto, je Brief)</span>
        {PREIS_FELDER.map((f) => (
          <label key={f.key} className="flex items-center justify-between" style={{ gap: 10, fontSize: 13 }}>
            <span>{f.label}</span>
            <span className="flex items-center" style={{ gap: 6 }}>
              <input inputMode="decimal" value={roh[f.key] ?? ''} placeholder="0,00" style={feld}
                onChange={(e) => setRoh((r) => ({ ...r, [f.key]: e.target.value }))} onBlur={() => speichern(f.key)} aria-label={f.label} />
              <span style={{ color: 'var(--ink3)' }}>€</span>
            </span>
          </label>
        ))}
        <span style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.5 }}>
          Die Post liefert über die Schnittstelle keine Preise. Gerechnet wird mit diesen Werten: Blätter aus der Seitenzahl (beidseitig = 2 Seiten je Blatt); bis 3 Blatt Standardbrief, 4–9 Blatt Kompaktbrief, ab 10 Blatt Großbrief, darüber hinaus „je weiteres Blatt“. Einschreiben: Zuschlag der Post plus der Unterschied zum normalen Briefporto. Gezählt wird, was die Post gedruckt hat, nach Druckmonat, wie auf ihrer Rechnung. Testbriefe, Fehler und Abgebrochene zählen nicht.
        </span>
      </div>

      {fehler && <span role="alert" style={{ fontSize: 12.5, color: 'var(--bad)' }}>{fehler}</span>}
      {ohnePreise ? (
        <span style={{ fontSize: 12.5, color: 'var(--warn)' }}>Noch keine Preise eingetragen – erst dann rechnet die Übersicht.</span>
      ) : !uebersicht ? (
        <div className="animate-pulse" style={{ height: 80, borderRadius: 14, background: 'var(--fill2)' }} />
      ) : (
        <div className="flex flex-col" style={{ gap: 6 }}>
          {uebersicht.offen.briefe > 0 && (
            <span style={{ fontSize: 12.5, color: 'var(--ink2)' }}>Noch nicht gedruckt: {uebersicht.offen.briefe} Briefe ≈ {euro(uebersicht.offen.summe)}</span>
          )}
          {uebersicht.monate.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>Noch keine gedruckten echten Briefe.</span>}
          {uebersicht.monate.map((m) => (
            <div key={m.monat} className="flex flex-col" style={{ gap: 4, padding: '8px 10px', borderRadius: 12, background: 'var(--fill)' }}>
              <button type="button" onClick={() => setOffenerMonat(offenerMonat === m.monat ? null : m.monat)} className="flex items-center justify-between cursor-pointer"
                style={{ gap: 8, border: 'none', background: 'transparent', color: 'var(--ink)', padding: 0, fontFamily: 'inherit' }}>
                <span style={{ fontSize: 13.5, fontWeight: 800 }}>{monatName(m.monat)}</span>
                <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 700 }}>{m.briefe} Briefe · {m.blaetter} Blatt · {euro(m.summe)}</span>
              </button>
              {offenerMonat === m.monat && m.nachObjekt.map((o) => (
                <span key={o.objekt} className="flex justify-between" style={{ fontSize: 12.5, color: 'var(--ink2)' }}>
                  <span className="truncate">{o.objekt}</span><span className="immo-zahl">{o.briefe} · {euro(o.summe)}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
    </CcWidget>
  );
}
